/**
 * WEB-2054 — КАРТА «вид блока → кусок рендерера».
 *
 * ЗАЧЕМ. Публичный рендерер блоков держал все 85 видов в одной связке файлов, и
 * сборщик отдавал её посетителю целиком: на этапе сборки он не знает, какая
 * воронка откроется. Покупатель качал и исполнял ветки, которых на его экранах
 * нет (портал подписок, скретч-карта, графики) — это прямой долг перед целью
 * WEB-2015 (150 КБ gzip первого экрана).
 *
 * КАК РЕЖЕМ:
 *  - ЯДРО (`PublicBlockRenderer.tsx`) — виды, которые есть почти в любой
 *    воронке: заголовки и текст, картинка, варианты выбора, поля ввода, кнопки
 *    «далее»/«по ссылке», прогресс, спиннер, таймер и денежные элементы
 *    пейволла (форма Stripe, кошельки, кнопка оплаты, цена, согласие с
 *    условиями, баннеры платёжных систем). Ядро едет с первым экраном.
 *  - `portal` — блоки портала управления подпиской: посетитель воронки их не
 *    видит НИКОГДА, они живут на отдельной странице по `manage_link`.
 *  - `reference` — восемь блоков разбора референсов (WEB-926): скретч-карта,
 *    blur-тизер, график-прогноз, промо-тикет, утверждение с реакциями,
 *    карточка предпочтения, полосы «сейчас/цель», шкала ИМТ. Каждый тянет свой
 *    компонент рисования; вместе — самая тяжёлая редкая семья.
 *  - `extended` — весь остальной хвост видов (`PublicBlockRendererExtended`).
 *
 * ПОЧЕМУ СЕМЬЯМИ, А НЕ ПО ВИДУ НА ФАЙЛ: кусок на каждый из 60 редких видов
 * превратил бы одну воронку в полсотни запросов к CDN — выигрыш в байтах
 * съелся бы задержками.
 *
 * ПОЧЕМУ ДАННЫМИ, А НЕ ЧЕРЕЗ САМИ КУСКИ: карту читают ДО загрузки кусков —
 * грелка по составу воронки (виды блоков известны из ответа резолва). Спросить
 * «какой кусок рисует этот вид» у самого куска значит уже его загрузить, то
 * есть загрузить все. Расхождение карты с кодом ловит двусторонний сторож
 * `publicBlockChunkMap.web2054.test.ts` (обходом исходников, не вторым списком).
 *
 * Файл лежит ВНЕ `components/editor/`: его читает публичный вход (страница
 * показа и роутер хоста) минуя рендерер, а туда ход закрыт правилом
 * `editor-bypassing-renderer` сторожа сборки.
 */
import type { BlockType } from '@/store/editorSlice';

/** Имена кусков. Ядро своего имени не имеет: оно не грузится отдельно. */
export const PUBLIC_BLOCK_CHUNK_NAMES = [
  'extended',
  'portal',
  'reference',
] as const;

export type PublicBlockChunkName = (typeof PUBLIC_BLOCK_CHUNK_NAMES)[number];

/**
 * Виды, которые рисует сам `PublicBlockRenderer.tsx`.
 * Список равен меткам `case '…'` того файла; равенство держит сторож.
 */
export const CORE_RENDERER_BLOCK_TYPES: readonly BlockType[] = [
  'title',
  'sub_title',
  'heading',
  'paragraph',
  'image',
  'html',
  'single_choice',
  'multiple_choice',
  'email',
  'text_field',
  'checkbox',
  // WEB-2158: явное согласие рисует ядро — блок стоит на экране КВИЗА рядом
  // с вопросом, то есть может оказаться на ПЕРВОМ экране покупателя.
  'sensitive_consent',
  'button_next',
  'button_link',
  'redirect_url',
  'progress_bar',
  'spinner',
  'terms_checkbox',
  'stripe_checkout_form',
  'stripe_wallets_express',
  'button_to_pay',
  'payment_banners',
  'result_screen',
  'price_block',
  'timer',
];

/**
 * WEB-2054 (починка ревизии, Р1 и Р4) — виды, которые тоже едут В ЯДРЕ, но
 * рисует их отдельный файл `PublicBlockRendererCommon.tsx` (ядро тянет его
 * СТАТИЧЕСКИ).
 *
 * ПОЧЕМУ ОНИ В ЯДРЕ:
 *  - деньги: `plan_selector` кладёт умолчание тарифа в значения формы только
 *    на своём монтировании, а лестница цены кнопки читает именно его — приезд
 *    позже кнопки означает чекаут не по тому тарифу; `payment_terms` и
 *    `price_breakdown_text` обещают сумму той же лестницей;
 *  - частота: `hero`, `bullet_list`, `icon_with_text`, `card`, `columns_2`,
 *    `testimonials` есть почти в любой воронке — отложить их значит не убрать
 *    вес, а переложить его во второй запрос на том же критическом пути.
 *
 * Список равен меткам `case '…'` файла `PublicBlockRendererCommon.tsx`, и
 * равенство — вместе со статичностью импорта — держит тот же сторож.
 */
export const CORE_COMMON_BLOCK_TYPES: readonly BlockType[] = [
  'plan_selector',
  'payment_terms',
  'price_breakdown_text',
  'hero',
  'bullet_list',
  'icon_with_text',
  'card',
  'columns_2',
  'testimonials',
];

/** Всё, что едет с первым экраном: свои ветки ядра плюс ядерный общий файл. */
export const CORE_BLOCK_TYPES: readonly BlockType[] = [
  ...CORE_RENDERER_BLOCK_TYPES,
  ...CORE_COMMON_BLOCK_TYPES,
];

/** Блоки портала управления подпиской (WEB-1152/WEB-1395). */
export const PORTAL_BLOCK_TYPES: readonly BlockType[] = [
  'subscription_list',
  'cancel_button',
  'resume_button',
  'downsell_offer',
];

/** Восемь блоков разбора референсов (WEB-926). */
export const REFERENCE_BLOCK_TYPES: readonly BlockType[] = [
  'scratch_card',
  'blur_teaser',
  'forecast_chart',
  'promo_ticket',
  'statement_agreement',
  'preference_card',
  'now_goal_bars',
  'bmi_scale',
];

/** Хвост видов, оставшийся в `PublicBlockRendererExtended`. */
export const EXTENDED_BLOCK_TYPES: readonly BlockType[] = [
  'divider',
  'spacer',
  'numbered_list',
  'accordion',
  'phone_input',
  'date_picker',
  'number_input',
  'select_dropdown',
  'rating_stars',
  'likert_scale',
  'rating_chip',
  'micro_feedback',
  'slider_range',
  'toggle',
  'name_field',
  'video_embed',
  'review_stars',
  'trust_badges',
  'guarantee_badge',
  'before_after',
  'gallery',
  'countdown',
  'stock_badge',
  'urgency_banner',
  'callout',
  'progress_text',
  'conditional_branch',
  'multi_step_question',
  'image_choice',
  'weight_picker',
  'height_picker',
  'loader_processing',
  'chart',
  'cookie_consent',
  'photo_upload',
  'downsell_popup',
  'return_to_app_button',
  'restore_purchases',
  'close_webview_button',
  'feature_chips',
];

const CHUNK_BY_TYPE: ReadonlyMap<string, PublicBlockChunkName> = new Map<
  string,
  PublicBlockChunkName
>([
  ...PORTAL_BLOCK_TYPES.map(
    (type) => [type, 'portal'] as [string, PublicBlockChunkName],
  ),
  ...REFERENCE_BLOCK_TYPES.map(
    (type) => [type, 'reference'] as [string, PublicBlockChunkName],
  ),
  ...EXTENDED_BLOCK_TYPES.map(
    (type) => [type, 'extended'] as [string, PublicBlockChunkName],
  ),
]);

/**
 * Кусок, который умеет рисовать этот вид блока. `null` = кусок не нужен: вид
 * либо в ядре, либо неизвестен вовсе.
 *
 * Неизвестный вид (воронка старше каталога, чужое поле) НЕ заказывает кусок «на
 * всякий случай»: рисовать его всё равно некому — обе ветки дойдут до `default`
 * и вернут пусто, а лишний запрос к CDN отнял бы у первого экрана время.
 */
export function publicBlockChunkOf(type: string): PublicBlockChunkName | null {
  return CHUNK_BY_TYPE.get(type) ?? null;
}

/**
 * Куски, нужные воронке такого состава, в порядке первой встречи вида.
 * Порядок не косметика: первым греется кусок того блока, который посетитель
 * увидит раньше.
 */
export function publicBlockChunksFor(
  types: Iterable<string>,
): PublicBlockChunkName[] {
  const wanted: PublicBlockChunkName[] = [];
  for (const type of types) {
    const chunk = publicBlockChunkOf(type);
    if (chunk && !wanted.includes(chunk)) wanted.push(chunk);
  }
  return wanted;
}
